import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  Home, CalendarDays, HeartPulse, Sparkles, MessageCircle, LogOut,
  Download, Phone, MapPin, ChevronRight, Clock, BellRing,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/clinic-app`;
const TOKEN_KEY = 'clinic_app_token';

const formatDate = (value) => (value ? new Date(value).toLocaleString('es-ES', { dateStyle: 'medium', timeStyle: 'short' }) : 'Sin fecha');
const formatDay = (value) => (value ? new Date(value).toLocaleDateString('es-ES', { dateStyle: 'medium' }) : 'Sin fecha');

const statusChip = {
  programada: 'bg-teal-100 text-teal-800',
  confirmada: 'bg-emerald-100 text-emerald-800',
  completada: 'bg-slate-200 text-slate-600',
  cancelada: 'bg-rose-100 text-rose-700',
  pendiente: 'bg-amber-100 text-amber-800',
  rechazada: 'bg-rose-100 text-rose-700',
};

const Card = ({ children, testId, className = '' }) => (
  <div className={`rounded-3xl border border-[#e8e2d6] bg-white p-5 shadow-[0_10px_30px_rgba(15,118,110,0.06)] ${className}`} data-testid={testId}>
    {children}
  </div>
);

export default function ClinicPatientAppPage() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || '');
  const [home, setHome] = useState(null);
  const [view, setView] = useState('inicio');
  const [loading, setLoading] = useState(false);
  const [loginForm, setLoginForm] = useState({ identifier: '', code: '' });
  const [requestForm, setRequestForm] = useState({ preferred_date: '', preferred_time: 'manana', reason: '' });
  const [installEvent, setInstallEvent] = useState(null);

  useEffect(() => {
    const link = document.createElement('link');
    link.rel = 'manifest';
    link.href = '/clinic-app-manifest.json';
    document.head.appendChild(link);
    const meta = document.createElement('meta');
    meta.name = 'theme-color';
    meta.content = '#0f766e';
    document.head.appendChild(meta);
    if ('serviceWorker' in navigator) navigator.serviceWorker.register('/clinic-sw.js').catch(() => {});
    return () => { document.head.removeChild(link); document.head.removeChild(meta); };
  }, []);

  useEffect(() => {
    const handler = (event) => { event.preventDefault(); setInstallEvent(event); };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const loadHome = useCallback(async (activeToken) => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/home`, { headers: { Authorization: `Bearer ${activeToken}` } });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'No se pudo cargar tu información');
      setHome(data);
    } catch (error) {
      toast.error(error.message);
      if (String(error.message).toLowerCase().includes('sesión')) {
        localStorage.removeItem(TOKEN_KEY);
        setToken('');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (token) loadHome(token); }, [token, loadHome]);

  const handleLogin = async (event) => {
    event.preventDefault();
    setLoading(true);
    try {
      const response = await fetch(`${API}/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(loginForm),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'No pudimos validar tus datos');
      localStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken('');
    setHome(null);
    setView('inicio');
  };

  const submitRequest = async (event) => {
    event.preventDefault();
    setLoading(true);
    try {
      const response = await fetch(`${API}/appointment-requests`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(requestForm),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'No se pudo enviar la solicitud');
      toast.success('Solicitud de cita enviada. La clínica te confirmará pronto.');
      setRequestForm({ preferred_date: '', preferred_time: 'manana', reason: '' });
      await loadHome(token);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const respondAppointment = async (appointmentId, action) => {
    if (action === 'cancel' && !window.confirm('¿Seguro que quieres cancelar esta cita?')) return;
    setLoading(true);
    try {
      const response = await fetch(`${API}/appointments/${appointmentId}/respond`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ action }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'No se pudo actualizar la cita');
      toast.success(action === 'confirm' ? 'Asistencia confirmada. ¡Te esperamos!' : 'Cita cancelada. La clínica queda avisada.');
      await loadHome(token);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleInstall = async () => {
    if (!installEvent) return;
    installEvent.prompt();
    await installEvent.userChoice;
    setInstallEvent(null);
  };

  const whatsappUrl = useMemo(() => {
    const number = home?.clinic?.whatsapp || '17872981758';
    const text = encodeURIComponent(`Hola, soy ${home?.patient?.name || 'paciente'} y quiero información de la clínica.`);
    return `https://wa.me/${number}?text=${text}`;
  }, [home]);

  if (!token) {
    return (
      <main className="min-h-screen bg-[#faf7f2] px-5 py-10" data-testid="clinic-app-login-page">
        <div className="mx-auto flex max-w-md flex-col items-center">
          <img src="/clinic-app-icon-192.png" alt="Clínica" className="h-20 w-20 rounded-3xl shadow-lg" />
          <p className="mt-6 text-xs font-semibold uppercase tracking-[0.3em] text-[#0f766e]">Clínica Dra. Sandra Alvarez</p>
          <h1 className="mt-2 text-center text-3xl font-black text-slate-900">Tu clínica, en tu móvil</h1>
          <p className="mt-2 text-center text-sm text-slate-500">Citas, tu historial de tratamientos, novedades y contacto directo.</p>
          <form onSubmit={handleLogin} className="mt-8 w-full space-y-4 rounded-3xl border border-[#e8e2d6] bg-white p-6 shadow-[0_20px_50px_rgba(15,118,110,0.08)]" data-testid="clinic-app-login-form">
            <div>
              <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Teléfono o DNI</label>
              <input
                data-testid="clinic-app-login-identifier"
                className="mt-1 w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm"
                placeholder="Ej: 933859360"
                value={loginForm.identifier}
                onChange={(event) => setLoginForm((prev) => ({ ...prev, identifier: event.target.value }))}
                required
              />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Código de acceso</label>
              <input
                data-testid="clinic-app-login-code"
                className="mt-1 w-full rounded-2xl border border-slate-200 px-4 py-3 text-center text-lg font-bold tracking-[0.4em]"
                placeholder="000000"
                value={loginForm.code}
                onChange={(event) => setLoginForm((prev) => ({ ...prev, code: event.target.value }))}
                required
              />
              <p className="mt-2 text-xs text-slate-400">La clínica te entrega tu código personal. Si no lo tienes, pídelo por WhatsApp.</p>
            </div>
            <button type="submit" disabled={loading} className="w-full rounded-full bg-[#0f766e] px-5 py-3.5 text-sm font-bold text-white transition hover:bg-[#0d5f59] disabled:opacity-50" data-testid="clinic-app-login-submit">
              {loading ? 'Entrando…' : 'Entrar a mi espacio'}
            </button>
          </form>
          {installEvent && (
            <button type="button" onClick={handleInstall} className="mt-5 inline-flex items-center gap-2 rounded-full border border-[#0f766e] px-5 py-2.5 text-sm font-semibold text-[#0f766e]" data-testid="clinic-app-install-button">
              <Download size={16} /> Instalar la app en tu móvil
            </button>
          )}
        </div>
      </main>
    );
  }

  const navItems = [
    { id: 'inicio', label: 'Inicio', icon: Home },
    { id: 'citas', label: 'Citas', icon: CalendarDays },
    { id: 'historial', label: 'Historial', icon: HeartPulse },
    { id: 'novedades', label: 'Novedades', icon: Sparkles },
    { id: 'contacto', label: 'Contacto', icon: MessageCircle },
  ];

  return (
    <main className="min-h-screen bg-[#faf7f2] pb-28" data-testid="clinic-app-page">
      <header className="sticky top-0 z-20 border-b border-[#e8e2d6] bg-[#faf7f2]/95 px-5 py-4 backdrop-blur">
        <div className="mx-auto flex max-w-md items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/clinic-app-icon-192.png" alt="Clínica" className="h-10 w-10 rounded-2xl" />
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-[#0f766e]">Clínica Dra. Sandra Alvarez</p>
              <p className="text-sm font-bold text-slate-900" data-testid="clinic-app-patient-name">{home?.patient?.name || 'Cargando…'}</p>
            </div>
          </div>
          <button type="button" onClick={handleLogout} className="rounded-full border border-slate-200 bg-white p-2.5 text-slate-500" data-testid="clinic-app-logout-button" aria-label="Salir">
            <LogOut size={16} />
          </button>
        </div>
      </header>

      <div className="mx-auto max-w-md space-y-4 px-5 py-5">
        {installEvent && (
          <button type="button" onClick={handleInstall} className="flex w-full items-center justify-center gap-2 rounded-full bg-[#0f766e] px-5 py-3 text-sm font-bold text-white" data-testid="clinic-app-install-banner">
            <Download size={16} /> Instalar la app en tu móvil
          </button>
        )}

        {view === 'inicio' && home && (
          <>
            {(() => {
              const reminder = home.notifications?.[0];
              const nextMs = home.next_appointment ? new Date(home.next_appointment.scheduled_at) - Date.now() : null;
              const soon = nextMs !== null && nextMs > 0 && nextMs <= 48 * 36e5;
              if (!reminder && !soon) return null;
              return (
                <div className="flex items-start gap-3 rounded-3xl border border-amber-200 bg-amber-50 p-4" data-testid="clinic-app-reminder-banner">
                  <BellRing size={18} className="mt-0.5 shrink-0 text-amber-600" />
                  <div>
                    <p className="text-xs font-bold uppercase tracking-wide text-amber-700">Recordatorio</p>
                    <p className="mt-1 text-sm text-amber-900">
                      {reminder ? reminder.message : `Tu cita es el ${formatDate(home.next_appointment.scheduled_at)}. Si no puedes asistir, avísanos por WhatsApp.`}
                    </p>
                  </div>
                </div>
              );
            })()}
            <Card testId="clinic-app-next-appointment-card" className="bg-gradient-to-br from-[#0f766e] to-[#115e59] !border-transparent text-white">
              <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-teal-100">Tu próxima cita</p>
              {home.next_appointment ? (
                <>
                  <p className="mt-2 text-xl font-black" data-testid="clinic-app-next-appointment-date">{formatDate(home.next_appointment.scheduled_at)}</p>
                  <p className="mt-1 text-sm text-teal-100">{home.next_appointment.appointment_type} · {home.next_appointment.duration_minutes || 45} min</p>
                  {home.next_appointment.patient_response === 'confirm' ? (
                    <p className="mt-3 inline-flex items-center gap-1 rounded-full bg-white/15 px-3 py-1.5 text-xs font-bold text-white" data-testid="clinic-app-attendance-confirmed">✓ Asistencia confirmada</p>
                  ) : (
                    <div className="mt-4 flex gap-2">
                      <button
                        type="button"
                        onClick={() => respondAppointment(home.next_appointment.id, 'confirm')}
                        disabled={loading}
                        className="flex-1 rounded-full bg-white px-4 py-2.5 text-xs font-bold text-[#0f766e] disabled:opacity-50"
                        data-testid="clinic-app-confirm-attendance"
                      >
                        Confirmo asistencia
                      </button>
                      <button
                        type="button"
                        onClick={() => respondAppointment(home.next_appointment.id, 'cancel')}
                        disabled={loading}
                        className="flex-1 rounded-full border border-white/40 px-4 py-2.5 text-xs font-bold text-white disabled:opacity-50"
                        data-testid="clinic-app-cancel-appointment"
                      >
                        No podré ir
                      </button>
                    </div>
                  )}
                </>
              ) : (
                <>
                  <p className="mt-2 text-lg font-bold">No tienes citas programadas</p>
                  <button type="button" onClick={() => setView('citas')} className="mt-3 inline-flex items-center gap-1 rounded-full bg-white px-4 py-2 text-sm font-bold text-[#0f766e]" data-testid="clinic-app-request-cta">
                    Pedir cita <ChevronRight size={14} />
                  </button>
                </>
              )}
            </Card>

            <div className="grid grid-cols-3 gap-3">
              <button type="button" onClick={() => setView('citas')} className="rounded-3xl border border-[#e8e2d6] bg-white p-4 text-center" data-testid="clinic-app-quick-citas">
                <CalendarDays className="mx-auto text-[#0f766e]" size={22} />
                <p className="mt-2 text-xs font-semibold text-slate-700">Pedir cita</p>
              </button>
              <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" className="rounded-3xl border border-[#e8e2d6] bg-white p-4 text-center" data-testid="clinic-app-quick-whatsapp">
                <MessageCircle className="mx-auto text-[#0f766e]" size={22} />
                <p className="mt-2 text-xs font-semibold text-slate-700">WhatsApp</p>
              </a>
              <button type="button" onClick={() => setView('novedades')} className="rounded-3xl border border-[#e8e2d6] bg-white p-4 text-center" data-testid="clinic-app-quick-novedades">
                <Sparkles className="mx-auto text-[#0f766e]" size={22} />
                <p className="mt-2 text-xs font-semibold text-slate-700">Novedades</p>
              </button>
            </div>

            <Card testId="clinic-app-summary-card">
              <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-[#0f766e]">Tu resumen</p>
              <div className="mt-3 grid grid-cols-2 gap-3">
                <div className="rounded-2xl bg-[#faf7f2] p-3">
                  <p className="text-xs text-slate-400">Sesiones realizadas</p>
                  <p className="text-2xl font-black text-slate-900" data-testid="clinic-app-total-sessions">{home.patient.total_sessions}</p>
                </div>
                <div className="rounded-2xl bg-[#faf7f2] p-3">
                  <p className="text-xs text-slate-400">Última visita</p>
                  <p className="text-sm font-bold text-slate-900">{formatDay(home.patient.last_movement_at)}</p>
                </div>
              </div>
              <p className="mt-3 text-xs text-slate-500">Sede habitual: <span className="font-semibold text-slate-700">{home.patient.primary_site || 'por asignar'}</span>{home.patient.preferred_assistant ? ` · Te atiende ${home.patient.preferred_assistant}` : ''}</p>
            </Card>

            {home.news?.length > 0 && (
              <Card testId="clinic-app-home-news">
                <div className="flex items-center justify-between">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-[#0f766e]">Novedades de la clínica</p>
                  <button type="button" onClick={() => setView('novedades')} className="text-xs font-semibold text-[#0f766e]">Ver todo</button>
                </div>
                <p className="mt-2 text-sm font-bold text-slate-900">{home.news[0].title}</p>
                <p className="mt-1 text-xs text-slate-500 line-clamp-2">{home.news[0].summary || home.news[0].content}</p>
              </Card>
            )}
          </>
        )}

        {view === 'citas' && home && (
          <>
            <Card testId="clinic-app-request-form-card">
              <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-[#0f766e]">Pedir cita</p>
              <h2 className="mt-1 text-lg font-black text-slate-900">Cuéntanos cuándo te viene bien</h2>
              <form onSubmit={submitRequest} className="mt-4 space-y-3">
                <input
                  data-testid="clinic-app-request-date"
                  type="date"
                  className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm"
                  value={requestForm.preferred_date}
                  onChange={(event) => setRequestForm((prev) => ({ ...prev, preferred_date: event.target.value }))}
                  required
                />
                <select
                  data-testid="clinic-app-request-time"
                  className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm"
                  value={requestForm.preferred_time}
                  onChange={(event) => setRequestForm((prev) => ({ ...prev, preferred_time: event.target.value }))}
                >
                  <option value="manana">Por la mañana</option>
                  <option value="tarde">Por la tarde</option>
                  <option value="indiferente">Me da igual</option>
                </select>
                <textarea
                  data-testid="clinic-app-request-reason"
                  className="min-h-[90px] w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm"
                  placeholder="Motivo: revisión, tratamiento, consulta…"
                  value={requestForm.reason}
                  onChange={(event) => setRequestForm((prev) => ({ ...prev, reason: event.target.value }))}
                  required
                />
                <button type="submit" disabled={loading} className="w-full rounded-full bg-[#0f766e] px-5 py-3 text-sm font-bold text-white disabled:opacity-50" data-testid="clinic-app-request-submit">
                  {loading ? 'Enviando…' : 'Enviar solicitud'}
                </button>
              </form>
            </Card>

            {home.my_requests?.length > 0 && (
              <Card testId="clinic-app-my-requests">
                <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-[#0f766e]">Mis solicitudes</p>
                <div className="mt-3 space-y-2">
                  {home.my_requests.map((req) => (
                    <div key={req.id} className="flex items-center justify-between rounded-2xl bg-[#faf7f2] p-3" data-testid={`clinic-app-request-item-${req.id}`}>
                      <div>
                        <p className="text-sm font-semibold text-slate-800">{req.preferred_date} · {req.preferred_time}</p>
                        <p className="text-xs text-slate-500 line-clamp-1">{req.reason}</p>
                      </div>
                      <span className={`rounded-full px-2.5 py-1 text-[10px] font-bold uppercase ${statusChip[req.status] || statusChip.pendiente}`}>{req.status}</span>
                    </div>
                  ))}
                </div>
              </Card>
            )}

            <Card testId="clinic-app-appointments-list">
              <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-[#0f766e]">Historial de citas</p>
              <div className="mt-3 space-y-2">
                {home.appointments?.length ? home.appointments.map((appointment) => (
                  <div key={appointment.id} className="flex items-center justify-between rounded-2xl bg-[#faf7f2] p-3" data-testid={`clinic-app-appointment-${appointment.id}`}>
                    <div>
                      <p className="text-sm font-semibold text-slate-800">{formatDate(appointment.scheduled_at)}</p>
                      <p className="text-xs text-slate-500">{appointment.appointment_type}{appointment.legacy_site ? ` · ${appointment.legacy_site}` : ''}</p>
                    </div>
                    <span className={`rounded-full px-2.5 py-1 text-[10px] font-bold uppercase ${statusChip[appointment.status] || 'bg-slate-100 text-slate-600'}`}>{appointment.status}</span>
                  </div>
                )) : <p className="text-sm text-slate-500">Aún no hay citas registradas.</p>}
              </div>
            </Card>
          </>
        )}

        {view === 'historial' && home && (
          <Card testId="clinic-app-visits-card">
            <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-[#0f766e]">Mis tratamientos</p>
            <h2 className="mt-1 text-lg font-black text-slate-900">Tu recorrido en la clínica</h2>
            <div className="mt-4 space-y-3">
              {home.visits?.length ? home.visits.map((visit) => (
                <div key={visit.id} className="rounded-2xl border border-[#eee8dc] bg-[#faf7f2] p-4" data-testid={`clinic-app-visit-${visit.id}`}>
                  <div className="flex items-center justify-between">
                    <p className="text-xs font-semibold text-[#0f766e]">{formatDay(visit.created_at)}</p>
                    {visit.legacy_site && <span className="inline-flex items-center gap-1 text-[10px] text-slate-400"><MapPin size={10} />{visit.legacy_site}</span>}
                  </div>
                  <p className="mt-1 text-sm font-bold text-slate-900">{visit.treatment_plan || visit.chief_complaint || 'Sesión clínica'}</p>
                  {visit.post_treatment_notes && <p className="mt-1 text-xs text-slate-500">{visit.post_treatment_notes}</p>}
                  {visit.follow_up_date && <p className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-amber-700"><Clock size={12} /> Seguimiento: {visit.follow_up_date}</p>}
                </div>
              )) : <p className="text-sm text-slate-500">Todavía no hay tratamientos registrados en tu ficha.</p>}
            </div>
          </Card>
        )}

        {view === 'novedades' && home && (
          <>
            {home.promotions?.length > 0 && (
              <Card testId="clinic-app-promotions-card" className="bg-gradient-to-br from-[#b4832d] to-[#8f6620] !border-transparent text-white">
                <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-amber-100">Promoción activa</p>
                <p className="mt-2 text-lg font-black">{home.promotions[0].title}</p>
                <p className="mt-1 text-sm text-amber-50">{home.promotions[0].message}</p>
                <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" className="mt-3 inline-flex rounded-full bg-white px-4 py-2 text-sm font-bold text-[#8f6620]" data-testid="clinic-app-promo-cta">
                  {home.promotions[0].cta_label || 'Me interesa'}
                </a>
              </Card>
            )}
            <div className="space-y-3" data-testid="clinic-app-news-list">
              {home.news?.length ? home.news.map((item) => (
                <Card key={item.id} testId={`clinic-app-news-${item.id}`}>
                  <div className="flex items-center gap-2">
                    <BellRing size={14} className="text-[#0f766e]" />
                    <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-400">{item.category || item.news_type || 'Novedad'}</p>
                  </div>
                  <p className="mt-2 text-sm font-bold text-slate-900">{item.title}</p>
                  <p className="mt-1 text-xs text-slate-500">{item.summary || item.content}</p>
                </Card>
              )) : <Card testId="clinic-app-news-empty"><p className="text-sm text-slate-500">Pronto verás aquí novedades y consejos de la clínica.</p></Card>}
            </div>
          </>
        )}

        {view === 'contacto' && home && (
          <>
            <Card testId="clinic-app-contact-card">
              <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-[#0f766e]">Habla con nosotros</p>
              <h2 className="mt-1 text-lg font-black text-slate-900">Estamos a un mensaje</h2>
              <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" className="mt-4 flex w-full items-center justify-center gap-2 rounded-full bg-[#25D366] px-5 py-3.5 text-sm font-bold text-white" data-testid="clinic-app-contact-whatsapp">
                <MessageCircle size={18} /> Escribir por WhatsApp
              </a>
              <a href={`tel:+${home.clinic.whatsapp}`} className="mt-3 flex w-full items-center justify-center gap-2 rounded-full border border-[#0f766e] px-5 py-3.5 text-sm font-bold text-[#0f766e]" data-testid="clinic-app-contact-call">
                <Phone size={16} /> Llamar {home.clinic.whatsapp_display}
              </a>
            </Card>
            <Card testId="clinic-app-sites-card">
              <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-[#0f766e]">Nuestras sedes</p>
              <div className="mt-3 space-y-2">
                {home.clinic.sites.map((site) => (
                  <div key={site} className="flex items-center gap-2 rounded-2xl bg-[#faf7f2] p-3">
                    <MapPin size={16} className="text-[#0f766e]" />
                    <p className="text-sm font-semibold text-slate-800">{site}</p>
                  </div>
                ))}
              </div>
            </Card>
          </>
        )}

        {loading && !home && <Card testId="clinic-app-loading"><p className="text-center text-sm text-slate-500">Cargando tu espacio…</p></Card>}
      </div>

      <nav className="fixed inset-x-0 bottom-0 z-30 border-t border-[#e8e2d6] bg-white/95 backdrop-blur" data-testid="clinic-app-bottom-nav">
        <div className="mx-auto flex max-w-md items-center justify-around px-2 py-2">
          {navItems.map((item) => {
            const Icon = item.icon;
            const active = view === item.id;
            return (
              <button
                key={item.id}
                type="button"
                onClick={() => setView(item.id)}
                className={`flex flex-col items-center gap-1 rounded-2xl px-3 py-2 transition ${active ? 'text-[#0f766e]' : 'text-slate-400'}`}
                data-testid={`clinic-app-nav-${item.id}`}
              >
                <Icon size={20} strokeWidth={active ? 2.6 : 2} />
                <span className={`text-[10px] font-semibold ${active ? 'text-[#0f766e]' : 'text-slate-400'}`}>{item.label}</span>
              </button>
            );
          })}
        </div>
      </nav>
    </main>
  );
}
